import { theme as antdTheme, type ThemeConfig } from 'antd';
import {
  brandRamp as ramp,
  fontFamily,
  neutral as neutralLight,
  neutralDark,
  shadow as shadowLight,
  shadowDark,
  shape,
  status as statusLight,
  statusDark,
  typeRamp,
} from '@wm/brand';

export { fontFamily };

/** The two themes of the portal; the user picks one, the system's preference decides until then. */
export type ThemeMode = 'light' | 'dark';

type Step = keyof typeof ramp;
type NeutralRole = keyof typeof neutralLight;
type ShadowRole = keyof typeof shadowLight;
type StatusRole = keyof typeof statusLight;

const kebab = (name: string) => name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
const steps = Object.keys(ramp).map(Number) as Step[];

/** `#rrggbb` mixed into `#rrggbb` (alpha of the first), as an opaque colour. */
function mix(top: string, bottom: string, alpha: number): string {
  const channel = (hex: string, i: number) => parseInt(hex.slice(1 + 2 * i, 3 + 2 * i), 16);
  const out = [0, 1, 2].map((i) =>
    Math.round(channel(top, i) * alpha + channel(bottom, i) * (1 - alpha)),
  );
  return `#${out.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The brand ramp read on the dark surfaces. A tint (130 to 160: selected rows, tiles, highlights)
 * becomes the brand laid thinly over the dark card, so it stays a quiet surface; a solid step
 * (10 to 120: icons, text, bars) moves towards the light end, so it stays as readable on dark as
 * it was on white: step 80, the brand itself, is read as step 110.
 */
const tintAlpha: Partial<Record<Step, number>> = { 130: 0.48, 140: 0.36, 150: 0.24, 160: 0.16 };
function darkStep(step: Step): string {
  const alpha = tintAlpha[step];
  if (alpha) return mix(ramp[80], neutralDark.background1, alpha);
  return ramp[Math.min(160, 190 - step) as Step];
}

/** Every colour role of one theme, from the shared tokens. */
interface Palette {
  neutral: Record<NeutralRole, string>;
  shadow: Record<ShadowRole, string>;
  status: Record<StatusRole, string>;
  brandStep: (step: Step) => string;
  /** the brand as a mark or text on the page's surfaces */
  brand: string;
  /** the brand as a filled background under white text (avatars, primary buttons) */
  brandBg: string;
  /** antd's filled surfaces (segmented tracks, tags, skeletons) */
  fill: [string, string, string];
}

const palettes: Record<ThemeMode, Palette> = {
  light: {
    neutral: neutralLight,
    shadow: shadowLight,
    status: statusLight,
    brandStep: (step) => ramp[step],
    brand: ramp[80],
    brandBg: ramp[80],
    fill: [neutralLight.background4, neutralLight.background3, neutralLight.background2],
  },
  dark: {
    neutral: neutralDark,
    shadow: shadowDark,
    status: statusDark,
    brandStep: darkStep,
    brand: ramp[110],
    brandBg: ramp[70],
    fill: ['rgba(255, 255, 255, 0.12)', 'rgba(255, 255, 255, 0.08)', 'rgba(255, 255, 255, 0.04)'],
  },
};

/** The custom properties of one theme: --wm-<role> for neutrals, shadows and statuses. */
function cssVariables(p: Palette): Record<string, string> {
  const vars: Record<string, string> = { '--wm-brand': p.brand, '--wm-brand-bg': p.brandBg };
  for (const [role, value] of Object.entries(p.neutral)) vars[`--wm-${kebab(role)}`] = value;
  for (const [role, value] of Object.entries(p.shadow)) vars[`--wm-${kebab(role)}`] = value;
  for (const [role, value] of Object.entries(p.status)) vars[`--wm-${kebab(role)}`] = value;
  for (const step of steps) vars[`--wm-brand-${step}`] = p.brandStep(step);
  return vars;
}

const declarations = (vars: Record<string, string>) =>
  Object.entries(vars)
    .map(([name, value]) => `${name}: ${value};`)
    .join(' ');

/**
 * Both themes as one style sheet: the light one on :root, the dark one when <html> carries
 * data-theme="dark" (set before the first paint by index.html, then by ThemeModeProvider).
 */
export const themeStyleSheet = [
  `:root { color-scheme: light; ${declarations(cssVariables(palettes.light))} }`,
  `:root[data-theme='dark'] { color-scheme: dark; ${declarations(cssVariables(palettes.dark))} }`,
].join('\n');

/* The tokens components use in their styles: CSS custom properties, so they follow the theme
   without a render. Same names and shapes as the tokens of @wm/brand. */

const cssVar = <K extends string | number>(roles: readonly K[], name: (role: K) => string) =>
  Object.fromEntries(roles.map((role) => [role, `var(${name(role)})`])) as Record<K, string>;

export const neutral = cssVar(
  Object.keys(neutralLight) as NeutralRole[],
  (r) => `--wm-${kebab(r)}`,
);
export const shadow = cssVar(Object.keys(shadowLight) as ShadowRole[], (r) => `--wm-${kebab(r)}`);
export const status = cssVar(Object.keys(statusLight) as StatusRole[], (r) => `--wm-${kebab(r)}`);
export const brandRamp = cssVar(steps, (s) => `--wm-brand-${s}`);

export const brand = {
  primary: 'var(--wm-brand)',
  /** under white text */
  background: 'var(--wm-brand-bg)',
  primaryDark: brandRamp[70],
  primarySoft: brandRamp[150],
  surface: neutral.background3,
  border: neutral.stroke2,
  text: neutral.foreground1,
  textMuted: neutral.foreground3,
  danger: status.danger,
  warning: status.warning,
  success: status.success,
  info: status.info,
};

const lh = ([size, line]: readonly [number, number]) => line / size;

/**
 * Ant Design theme of the application, on Fluent 2 (fluent2.microsoft.design): the brand ramp
 * through Fluent's alias roles (light: rest 80, hover 70, pressed 40, tints 160 and 150; dark: rest
 * 70, hover 80, pressed 40, text 110), controls that change shade as they are used, grey neutrals,
 * the web type ramp, 4/8 px corners, 32 px controls and the key + ambient shadow ramp (cards 4,
 * menus 16, side sheets 28, dialogs 64).
 */
export function appTheme(mode: ThemeMode): ThemeConfig {
  const p = palettes[mode];
  const n = p.neutral;
  const dark = mode === 'dark';
  const tint = { bg: p.brandStep(160), bgHover: p.brandStep(150) };
  const text = dark
    ? { rest: ramp[110], hover: ramp[120], pressed: ramp[100] }
    : { rest: ramp[80], hover: ramp[70], pressed: ramp[40] };
  const link = dark ? text : { rest: ramp[70], hover: ramp[60], pressed: ramp[40] };
  return {
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: p.brandBg,
      colorPrimaryHover: dark ? ramp[80] : ramp[70],
      colorPrimaryActive: ramp[40],
      colorPrimaryBg: tint.bg,
      colorPrimaryBgHover: tint.bgHover,
      colorPrimaryBorder: p.brandStep(140),
      colorPrimaryBorderHover: p.brandStep(120),
      colorPrimaryText: text.rest,
      colorPrimaryTextHover: text.hover,
      colorPrimaryTextActive: text.pressed,
      controlItemBgActive: tint.bg,
      controlItemBgActiveHover: tint.bgHover,
      controlItemBgHover: n.background1Hover,
      controlOutline: 'transparent',
      colorInfo: p.status.info,
      colorSuccess: p.status.success,
      colorSuccessBg: p.status.successBg,
      colorSuccessBorder: p.status.successBorder,
      colorWarning: p.status.warning,
      colorError: p.status.danger,
      // Fluent links use a darker brand step on white, a lighter one on dark, and shift on hover
      colorLink: link.rest,
      colorLinkHover: link.hover,
      colorLinkActive: link.pressed,
      colorText: n.foreground1,
      colorTextSecondary: n.foreground3,
      colorTextTertiary: n.foreground4,
      colorTextQuaternary: n.foregroundDisabled,
      colorTextDisabled: n.foregroundDisabled,
      colorTextPlaceholder: n.foreground4,
      colorIcon: n.foreground3,
      colorIconHover: n.foreground1,
      colorBorder: n.stroke1,
      colorBorderSecondary: n.stroke2,
      colorSplit: n.stroke2,
      colorFillSecondary: p.fill[0],
      colorFillTertiary: p.fill[1],
      colorFillQuaternary: p.fill[2],
      colorBgLayout: n.background3,
      colorBgContainer: n.background1,
      colorBgElevated: dark ? n.background6 : n.background1,
      colorBgContainerDisabled: dark ? n.background2 : n.background3,
      colorBgTextHover: n.background1Hover,
      colorBgTextActive: n.background1Pressed,
      fontFamily,
      fontSize: shape.fontSize,
      fontSizeSM: typeRamp.caption1[0],
      fontSizeLG: typeRamp.subtitle2[0],
      fontSizeXL: typeRamp.subtitle1[0],
      lineHeight: lh(typeRamp.body1),
      lineHeightSM: lh(typeRamp.caption1),
      lineHeightLG: lh(typeRamp.subtitle2),
      fontSizeHeading1: typeRamp.largeTitle[0],
      fontSizeHeading2: typeRamp.title1[0],
      fontSizeHeading3: typeRamp.title2[0],
      fontSizeHeading4: typeRamp.title3[0],
      fontSizeHeading5: typeRamp.subtitle1[0],
      lineHeightHeading1: lh(typeRamp.largeTitle),
      lineHeightHeading2: lh(typeRamp.title1),
      lineHeightHeading3: lh(typeRamp.title2),
      lineHeightHeading4: lh(typeRamp.title3),
      lineHeightHeading5: lh(typeRamp.subtitle1),
      fontWeightStrong: 600,
      borderRadius: shape.radius,
      borderRadiusXS: shape.radiusSM,
      borderRadiusSM: shape.radiusSM,
      borderRadiusLG: shape.radiusLG,
      borderRadiusOuter: shape.radius,
      controlHeight: shape.controlHeight,
      controlHeightSM: 24,
      controlHeightLG: 40,
      lineWidthFocus: 2,
      boxShadow: p.shadow.shadow28,
      boxShadowSecondary: p.shadow.shadow16,
      boxShadowTertiary: p.shadow.shadow4,
      motionDurationFast: '0.1s',
      motionDurationMid: '0.2s',
      motionDurationSlow: '0.3s',
      wireframe: false,
    },
    components: {
      Layout: {
        siderBg: n.background1,
        lightSiderBg: n.background1,
        headerBg: n.background1,
        headerHeight: 52,
        headerPadding: '0 16px',
        bodyBg: n.background3,
      },
      // the Fluent navigation pane: grey text, a subtle hover, the selected item in the brand tint
      // with semibold text (its indicator bar is drawn in styles.css)
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: n.foreground2,
        itemHoverColor: n.foreground1,
        itemHoverBg: n.background1Hover,
        itemActiveBg: n.background1Pressed,
        itemSelectedBg: tint.bg,
        itemSelectedColor: n.foreground1,
        groupTitleColor: n.foreground3,
        groupTitleFontSize: typeRamp.caption1[0],
        itemBorderRadius: shape.radius,
        itemMarginInline: 8,
        itemMarginBlock: 2,
        itemHeight: 36,
        iconSize: 16,
        collapsedIconSize: 16,
        activeBarWidth: 0,
        activeBarHeight: 0,
        activeBarBorderWidth: 0,
      },
      Button: {
        fontWeight: 600,
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
        defaultBg: n.background1,
        defaultBorderColor: n.stroke1,
        defaultColor: n.foreground1,
        defaultHoverBg: n.background1Hover,
        defaultHoverColor: n.foreground1,
        defaultHoverBorderColor: n.stroke1Hover,
        defaultActiveBg: n.background1Pressed,
        defaultActiveColor: n.foreground1,
        defaultActiveBorderColor: n.stroke1Pressed,
        textHoverBg: n.background1Hover,
        textTextHoverColor: n.foreground1,
        textTextActiveColor: n.foreground1,
        paddingInline: 12,
      },
      Input: {
        activeBorderColor: n.stroke1,
        hoverBorderColor: n.stroke1Hover,
        activeShadow: 'none',
      },
      InputNumber: {
        activeBorderColor: n.stroke1,
        hoverBorderColor: n.stroke1Hover,
        activeShadow: 'none',
      },
      Select: {
        activeBorderColor: n.stroke1,
        hoverBorderColor: n.stroke1Hover,
        activeOutlineColor: 'transparent',
        optionSelectedBg: tint.bg,
        optionSelectedColor: n.foreground1,
        optionSelectedFontWeight: 600,
        optionActiveBg: n.background1Hover,
      },
      DatePicker: {
        activeBorderColor: n.stroke1,
        hoverBorderColor: n.stroke1Hover,
        activeShadow: 'none',
      },
      Card: {
        headerFontSize: typeRamp.subtitle2[0],
        headerHeight: 48,
        paddingLG: 20,
      },
      Table: {
        headerBg: n.background2,
        headerColor: n.foreground2,
        headerSplitColor: 'transparent',
        rowHoverBg: n.background1Hover,
        rowSelectedBg: tint.bg,
        rowSelectedHoverBg: tint.bgHover,
        borderColor: n.stroke2,
        cellPaddingBlock: 12,
        cellPaddingInline: 12,
      },
      Tabs: {
        itemColor: n.foreground2,
        itemHoverColor: n.foreground1,
        itemSelectedColor: n.foreground1,
        inkBarColor: p.brand,
        titleFontSize: shape.fontSize,
      },
      Segmented: {
        itemColor: n.foreground2,
        itemHoverColor: n.foreground1,
        itemHoverBg: n.background1Hover,
        itemSelectedColor: n.foreground1,
        itemSelectedBg: dark ? n.background1Selected : n.background1,
        trackBg: dark ? n.background3 : n.background4,
      },
      Tag: { borderRadiusSM: shape.radius, defaultBg: n.background3 },
      Breadcrumb: {
        itemColor: n.foreground3,
        lastItemColor: n.foreground1,
        linkColor: n.foreground3,
        linkHoverColor: n.foreground1,
        separatorColor: n.foreground3,
      },
      TreeSelect: { nodeSelectedBg: tint.bg },
      Tree: { nodeSelectedBg: tint.bg, nodeHoverBg: n.background1Hover },
      Modal: { boxShadow: p.shadow.shadow64, titleFontSize: typeRamp.subtitle1[0] },
      Drawer: { paddingLG: 24 },
      Dropdown: { controlItemBgHover: n.background1Hover },
      Tooltip: { colorBgSpotlight: dark ? n.background6 : n.foreground1 },
      Statistic: { contentFontSize: typeRamp.title2[0] },
      Alert: { withDescriptionPadding: '12px 16px' },
    },
  };
}
